import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  getOverallImpact,
  initiativeErrorMessage,
} from "../services/efficiencyInitiativesApi";

/**
 * Overall Impact — portfolio-wide totals, ROI and the cumulative benefit curve.
 *
 * Three things this panel refuses to do, all deliberate:
 *
 *   * It does not print a zero for anything it has not measured. CO2 has no
 *     agreed emission factor, and insight loss is not computed until asked for; a
 *     zero total reads as "we measured it and it was nothing".
 *   * It does not quietly sum overlapping initiatives. Where one initiative's node
 *     sits inside another's, the same kWh is counted twice; the totals still add
 *     up as the specification asks, but the overlap is shown above them.
 *   * It does not present a partial insight-loss total as a portfolio total. That
 *     pass is capped and time-budgeted server-side, so the number is always
 *     accompanied by how many initiatives are actually behind it.
 *
 * Insight loss is loaded on demand rather than with the panel. It costs a detector
 * pass per category per window per initiative — tens of seconds — and this panel
 * is what the tab opens on, so paying that on every open would make the dashboard
 * feel broken for a figure most visits do not need.
 */

const PKR = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 });
const NUM = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 1 });

const fmtPkr = (v) => (v == null ? "—" : `PKR ${PKR.format(v)}`);
const fmtKwh = (v) => (v == null ? "—" : `${PKR.format(v)} kWh`);
const fmtPct = (v) => (v == null ? "—" : `${NUM.format(v)}%`);
const fmtMonths = (v) =>
  v == null ? "No payback" : v < 24 ? `${NUM.format(v)} months` : `${NUM.format(v / 12)} years`;

export default function OverallImpactPanel({ username, statusFilter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sticky for the life of the panel: once the user has asked for insight loss,
  // changing the status filter should not silently drop it again.
  const [wantInsightLoss, setWantInsightLoss] = useState(false);
  const [lossLoading, setLossLoading] = useState(false);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    setLoading(true);
    setLossLoading(wantInsightLoss);
    setError(null);
    getOverallImpact(username, statusFilter, { includeInsightLoss: wantInsightLoss })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(initiativeErrorMessage(err, "Could not compute overall impact."));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLossLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [username, statusFilter, wantInsightLoss]);

  // Cumulative benefit against cumulative spend. The crossover is the portfolio's
  // real payback moment, which is why both series share one axis rather than being
  // shown as separate charts.
  const chart = useMemo(() => {
    const rows = data?.cumulative || [];
    if (rows.length === 0) return null;
    const toPoint = (r, field) => ({ x: new Date(r.period).getTime(), y: r[field] });
    return {
      series: [
        { name: "Cumulative saving", data: rows.map((r) => toPoint(r, "cumulative_saving_pkr")) },
        // The same accrual restricted to days with meter data. Drawn because the
        // tiles above report THIS basis, and without it the chart appears to
        // contradict them by however much meter downtime there was.
        { name: "Measured only", data: rows.map((r) => toPoint(r, "measured_saving_pkr")) },
        { name: "Cumulative cost", data: rows.map((r) => toPoint(r, "cumulative_cost_pkr")) },
        { name: "Net position", data: rows.map((r) => toPoint(r, "cumulative_net_pkr")) },
      ],
      options: {
        chart: { type: "line", height: 300, toolbar: { show: false }, animations: { enabled: false } },
        // Four series, four entries in each of these: a short array makes
        // ApexCharts recycle, which would have drawn "Measured only" in the cost
        // series' orange. Order matches the series order above.
        colors: ["#1ba08e", "#7dd3c8", "#f97316", "#6366f1"],
        // "Measured only" is the same quantity on a stricter basis, so it is a
        // lighter dashed version of the saving line rather than a new colour with
        // no relationship to it.
        stroke: { width: [3, 2, 2, 2], curve: "smooth", dashArray: [0, 4, 5, 0] },
        dataLabels: { enabled: false },
        markers: { size: 4 },
        xaxis: { type: "datetime", labels: { format: "MMM yyyy", style: { fontSize: "11px" } } },
        yaxis: {
          title: { text: "PKR" },
          labels: { formatter: (v) => PKR.format(v) },
        },
        // Zero matters here: it is the line the net position has to cross.
        annotations: {
          yaxis: [{ y: 0, borderColor: "#94a3b8", strokeDashArray: 3 }],
        },
        legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
        grid: { borderColor: "#e2e8f0", strokeDashArray: 3 },
        tooltip: { x: { format: "MMM yyyy" }, y: { formatter: (v) => `PKR ${PKR.format(v)}` } },
      },
    };
  }, [data]);

  // Only blank the panel when there is nothing to show. Asking for insight loss
  // triggers a refetch that can run for the better part of a minute, and throwing
  // away the totals the user is already reading to show a spinner in their place
  // would be a worse trade than leaving them up with a banner over them.
  if (loading && !data) {
    return (
      <div className="ei-card">
        <p className="ei-hint">Computing measured impact across every initiative…</p>
      </div>
    );
  }
  if (error && !data) {
    return <div className="ei-card"><div className="ei-alert error">{error}</div></div>;
  }
  if (!data) return null;

  const t = data.totals;
  const f = t.financial;
  const dc = data.double_counting;
  const loss = t.insight_loss || {};
  // Partial in two different ways, and the distinction matters to the reader:
  // skipped initiatives were never attempted, timed-out ones were attempted and
  // abandoned. Either way the total below covers less than the portfolio.
  const lossPartial =
    loss.available &&
    (loss.measured_initiatives ?? 0) < (loss.initiatives_considered ?? 0);

  return (
    <div className="ei-card">
      <div className="ei-impact-head">
        <h4>
          Overall impact{" "}
          <span className="ei-impact-sub">
            {data.measurable_count} of {data.initiative_count} initiatives measurable ·
            cancelled excluded
          </span>
        </h4>
      </div>

      {(dc.overlaps || []).length > 0 && (
        <div className="ei-alert warn">
          <strong title={dc.note}>Totals may include double counting.</strong>
          <div style={{ marginTop: "0.35rem", fontWeight: 400 }}>
            <ul style={{ margin: 0, paddingLeft: "1rem" }}>
              {dc.overlaps.map((o, i) => (
                <li key={i}>
                  <strong>{o.outer}</strong> and <strong>{o.inner}</strong> ({o.relationship}) —{" "}
                  {o.detail}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Shown apart from the node overlaps above, and worded more strongly.
          A nested node means the same kWh *may* be counted twice; two initiatives
          claiming the same insight on the same or nested nodes over overlapping
          periods means they are describing one fix twice. This does not depend on
          the insight-loss pass having run — it compares what each initiative
          claims, not what was measured — so it appears on every load. */}
      {(dc.shared_insight_overlaps || []).length > 0 && (
        <div className="ei-alert warn">
          <strong title={dc.shared_insight_note}>Initiatives claiming the same fix.</strong>
          <div style={{ marginTop: "0.35rem", fontWeight: 400 }}>
            <ul style={{ margin: 0, paddingLeft: "1rem" }}>
              {dc.shared_insight_overlaps.map((o, i) => (
                <li key={i}>
                  <strong>{(o.initiatives || []).join(" and ")}</strong> ({o.relationship})
                  — both claim {(o.shared_insights || []).join(", ")}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {data.not_measurable_count > 0 && (
        <div className="ei-alert info">
          <span title="Their cost is still counted, so ROI is not flattered.">
            {data.not_measurable_count} initiative
            {data.not_measurable_count === 1 ? "" : "s"} could not be measured.
          </span>
        </div>
      )}

      <div className="ei-kpis">
        <Tile label="Total energy saved" value={fmtKwh(t.energy.realised_saving_kwh)}
              sub={`${fmtKwh(t.energy.annualised_saving_kwh)}/yr run-rate`} good />
        <Tile label="Total financial savings" value={fmtPkr(f.realised_saving_pkr)}
              sub={`${fmtPkr(f.annualised_saving_pkr)}/yr run-rate`} good />
        <Tile label="Total implementation cost" value={fmtPkr(f.implementation_cost_pkr)}
              sub="all initiatives, measurable or not" />
        <Tile label="Overall ROI" value={fmtPct(f.overall_roi_percent)}
              sub={`${fmtPct(f.annualised_roi_percent)} annualised`}
              good={f.overall_roi_percent != null && f.overall_roi_percent > 0} />
        <Tile label="Payback period" value={fmtMonths(f.overall_payback_months)}
              sub="portfolio-wide, on the run-rate"
              good={f.overall_payback_months != null} />
        <Tile label="Net position" value={fmtPkr(f.net_position_pkr)}
              sub={f.net_position_pkr >= 0 ? "cost recovered" : "still recovering"}
              good={f.net_position_pkr >= 0} />
        <Tile label="Total peak reduction"
              value={`${NUM.format(t.peak_demand.total_reduction_kw)} kW`}
              sub={`${t.peak_demand.contributing_initiatives} initiatives`}
              good={t.peak_demand.total_reduction_kw > 0} />
        <Tile label="Total CO₂ avoided" value="N/A" sub="no emission factor set" good />
        {/* Three states, never collapsed into one: measured, still computing, and
            not asked for. "Not asked for" must not look like "measured zero", so
            it renders as an action rather than a number. */}
        {loss.available ? (
          <Tile
            label="Total loss reduction"
            value={fmtPkr(loss.total_reduction_pkr)}
            sub={`${fmtPkr(loss.annualised_reduction_pkr)}/yr · ${
              loss.measured_initiatives
            } of ${loss.initiatives_considered} measured`}
            good={(loss.annualised_reduction_pkr || 0) > 0}
          />
        ) : (
          <div className="ei-kpi neutral">
            <p className="ei-kpi-label">Total loss reduction</p>
            <p className="ei-kpi-value">{lossLoading ? "…" : "—"}</p>
            <p className="ei-kpi-sub">
              {lossLoading ? (
                "running the insight detectors"
              ) : wantInsightLoss ? (
                loss.reason || "not available"
              ) : (
                <button className="ei-link-btn" onClick={() => setWantInsightLoss(true)}>
                  Calculate
                </button>
              )}
            </p>
          </div>
        )}
      </div>

      {/* A refetch that failed while stale data is on screen: the numbers below
          are still the previous successful response, so say so rather than
          letting them pass as current. */}
      {error && (
        <div className="ei-alert error">
          {error} The figures below are from the previous successful load.
        </div>
      )}

      {lossLoading && (
        <div className="ei-alert info">
          Comparing insights before and after implementation — up to a minute.
        </div>
      )}

      {lossPartial && (
        <div className="ei-alert info">
          <strong>
            Loss reduction covers {loss.measured_initiatives} of{" "}
            {loss.initiatives_considered} initiatives.
          </strong>
          <div style={{ marginTop: "0.3rem", fontWeight: 400 }}>
            A floor, not the portfolio figure. Open an initiative to measure it.
          </div>
        </div>
      )}

      {/* No per-insight breakdown here on purpose. The dashboard question is
          "which initiative earned this"; the per-insight attribution is on each
          initiative's detail view, where a regressing insight can be shown with
          the context that explains it. The rollup still returns by_insight for
          callers that want it. */}

      {chart && (
        <>
          <p className="ei-detail-label">Cumulative impact over time</p>
          <div className="ei-impact-chart">
            <ReactApexChart options={chart.options} series={chart.series} type="line" height={300} />
          </div>
          {/* The basis is a tooltip, not a paragraph: it is long, and it only
              matters to someone already asking why the two saving lines differ. */}
          <p className="ei-rank-note" title={data.cumulative_basis}>
            Net position crossing zero is the payback point. "Measured only" counts
            days with meter data — the tiles above use that basis.
          </p>
        </>
      )}

      {data.contributors.length > 0 && (
        <>
          <p className="ei-detail-label">Contribution by initiative</p>
          <div className="ei-table-wrapper">
            <table className="ei-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Initiative</th>
                  <th>Node</th>
                  <th className="ei-num">Energy (kWh/yr)</th>
                  <th className="ei-num">Financial (PKR/yr)</th>
                  {/* Loss reduction belongs here, per initiative, not in a
                      separate per-insight table: this screen answers "which
                      initiative earned this", and the per-insight split is already
                      on each initiative's own detail view where it has room to be
                      read properly. */}
                  <th className="ei-num">Loss reduction (PKR/yr)</th>
                  <th className="ei-num">Cost (PKR)</th>
                  <th className="ei-num">Share of savings</th>
                </tr>
              </thead>
              <tbody>
                {data.contributors.map((c) => {
                  const share = f.annualised_saving_pkr
                    ? (c.financial.annualised_saving_pkr / f.annualised_saving_pkr) * 100
                    : null;
                  const lr = c.loss_reduction;
                  return (
                    <tr key={c.initiative_id} className="ei-row">
                      <td className="ei-ref">{c.reference_code}</td>
                      <td><div className="ei-title-cell">{c.title}</div></td>
                      <td>
                        <span className={`ei-badge node-${c.node_type}`}>{c.node_type}</span>
                        <div className="ei-title-sub">{c.node_name}</div>
                      </td>
                      <td className="ei-num">{PKR.format(c.energy.annualised_saving_kwh)}</td>
                      <td className="ei-num">{PKR.format(c.financial.annualised_saving_pkr)}</td>
                      <td className="ei-num">
                        {lr?.available ? (
                          <strong className={lr.annualised_pkr > 0 ? "ei-good" : "ei-bad"}>
                            {PKR.format(lr.annualised_pkr)}
                          </strong>
                        ) : (
                          <span className="ei-hint" title={lr?.reason}>
                            {wantInsightLoss ? "not measured" : "—"}
                          </span>
                        )}
                      </td>
                      <td className="ei-num">{PKR.format(c.implementation_cost_pkr || 0)}</td>
                      <td className="ei-num">{share == null ? "—" : `${NUM.format(share)}%`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data.not_measurable.length > 0 && (
        <div style={{ marginTop: "1.25rem" }}>
          <p className="ei-detail-label">Not yet measurable (cost counted, saving not)</p>
          <ul className="ei-rank-list">
            {data.not_measurable.map((n) => (
              <li key={n.initiative_id}>
                <strong>{n.reference_code}</strong> {n.title}
                <div className="ei-title-sub">{n.reason}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, sub, good }) {
  return (
    <div className={`ei-kpi${good ? "" : " neutral"}`}>
      <p className="ei-kpi-label">{label}</p>
      <p className="ei-kpi-value">{value}</p>
      <p className="ei-kpi-sub">{sub}</p>
    </div>
  );
}
